const SSR_MESSAGE = 'Sal was not initialised! Probably it is used in SSR.';
const NOT_SUPPORTED_MESSAGE = `
Your browser does not support IntersectionObserver!
Get a polyfill from here:
https://github.com/w3c/IntersectionObserver/tree/master/polyfill
`;

// Default options
let options = {
  root: null,
  rootMargin: '0% 50%',
  threshold: 0.5,
  animateClassName: 'sal-animate',
  disabledClassName: 'sal-disabled',
  enterEventName: 'sal:in',
  exitEventName: 'sal:out',
  selector: '[data-sal]',
  observerSelector: '.sal-observer',
  once: true,
  disabled: false,
};

let elements = [];
let intersectionObserver = null;

const setOptions = (settings = {}) => {
  options = { ...options, ...settings };
};

const clearAnimation = (element) => {
  element.querySelector(options.selector)?.classList.remove(options.animateClassName);
};

const fireEvent = (name, entry) => {
  const event = new CustomEvent(name, {
    bubbles: true,
    detail: entry,
  });
  entry.target.dispatchEvent(event);
};

const animate = (entry) => {
  entry.target.querySelector(options.selector)?.classList.add(options.animateClassName);
  fireEvent(options.enterEventName, entry);
};

const reverse = (entry) => {
  clearAnimation(entry.target);
  fireEvent(options.exitEventName, entry);
};

const isAnimated = (element) => element.classList.contains(options.animateClassName);

const enableAnimations = () => {
  document.body?.classList.remove(options.disabledClassName);
};

const disableAnimations = () => {
  document.body.classList.add(options.disabledClassName);
};

const clearObserver = () => {
  if (intersectionObserver) {
    intersectionObserver.disconnect();
    intersectionObserver = null;
  }
};

const isDisabled = () => (typeof options.disabled === 'function' ? options.disabled() : options.disabled);

const onIntersection = (entries, observer) => {
  entries.forEach((entry) => {
    const { target, intersectionRatio } = entry;
    const hasRepeatFlag = 'salRepeat' in target.dataset;
    const hasOnceFlag = 'salOnce' in target.dataset;
    const shouldRepeat = hasRepeatFlag || !(hasOnceFlag || options.once);

    if (intersectionRatio >= options.threshold) {
      animate(entry);
      if (!shouldRepeat) {
        observer.unobserve(target);
      }
    } else if (shouldRepeat) {
      reverse(entry);
    }
  });
};

const getObservedElements = () => {
  const collection = Array.from(document.querySelectorAll(options.observerSelector)).filter((el) => !isAnimated(el));
  collection.forEach((element) => intersectionObserver.observe(element));

  return collection;
};

const disable = () => {
  disableAnimations();
  clearObserver();
};

const enable = () => {
  enableAnimations();

  intersectionObserver = new IntersectionObserver(onIntersection, {
    root: options.root,
    rootMargin: options.rootMargin,
    threshold: options.threshold,
  });

  elements = getObservedElements();
};

const reset = (settings = {}) => {
  clearObserver();
  document.querySelectorAll(options.selector).forEach(clearAnimation);
  setOptions(settings);
  enable();
};

const update = () => {
  const newElements = getObservedElements();
  elements.push(...newElements);
};

export const init = (settings = {}) => {
  setOptions(settings);

  if (typeof window === 'undefined') {
    console.warn(SSR_MESSAGE);
    return { elements, disable, enable, reset, update };
  }

  if (!('IntersectionObserver' in window)) {
    disableAnimations();
    throw new Error(NOT_SUPPORTED_MESSAGE);
  }

  if (!isDisabled()) {
    enable();
  } else {
    disableAnimations();
  }

  return { elements, disable, enable, reset, update };
};
